// Donation Service - Handles PayPal and M-Pesa integrations
import axios from 'axios';

// PayPal Service
class PayPalService {
  static createOrder(amount, currency = 'USD') {
    return {
      intent: 'CAPTURE',
      purchase_units: [{
        amount: {
          currency_code: currency,
          value: amount.toString()
        },
        description: `Donation to ${process.env.REACT_APP_ORGANIZATION_NAME || 'WeFlare'} - $${amount}`,
        custom_id: `donation-${Date.now()}`,
        soft_descriptor: 'WeFlare Donation'
      }],
      application_context: {
        brand_name: process.env.REACT_APP_ORGANIZATION_NAME || 'WeFlare',
        landing_page: 'BILLING',
        user_action: 'PAY_NOW',
        return_url: `${process.env.REACT_APP_BASE_URL || window.location.origin}/donation/success`,
        cancel_url: `${process.env.REACT_APP_BASE_URL || window.location.origin}/donation/cancel`
      }
    };
  }

  static async onApprove(data, actions) {
    try {
      const order = await actions.order.capture();
      console.log('PayPal payment successful:', order);
      
      // Send confirmation to your backend
      await PayPalService.recordDonation({
        orderId: order.id,
        amount: order.purchase_units[0].amount.value,
        currency: order.purchase_units[0].amount.currency_code,
        payerEmail: order.payer.email_address,
        payerName: `${order.payer.name.given_name} ${order.payer.name.surname}`,
        status: order.status,
        timestamp: new Date().toISOString()
      });

      return order;
    } catch (error) {
      console.error('PayPal capture error:', error);
      throw error;
    }
  }

  static onError(error) {
    console.error('PayPal error:', error);
    throw new Error('PayPal payment failed. Please try again.');
  }

  static onCancel(data) {
    console.log('PayPal payment cancelled:', data);
    return { cancelled: true };
  }

  static async recordDonation(donationData) {
    try {
      // This would typically send to your backend
      const response = await axios.post('/api/donations/paypal', donationData);
      return response.data;
    } catch (error) {
      console.error('Failed to record PayPal donation:', error);
      // Don't throw error here as payment was successful
    }
  }
}

// M-Pesa Service
class MpesaService {
  static formatPhoneNumber(phoneNumber) {
    // Remove any non-digit characters
    let cleaned = phoneNumber.replace(/\D/g, '');
    
    // Handle different formats
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    } else if (cleaned.startsWith('+254')) {
      cleaned = cleaned.substring(1);
    } else if (cleaned.startsWith('254')) {
      // Already in correct format
    } else if (cleaned.length === 9) {
      cleaned = '254' + cleaned;
    }
    
    return cleaned;
  }

  static validatePhoneNumber(phoneNumber) {
    const formatted = MpesaService.formatPhoneNumber(phoneNumber);
    return /^254[17]\d{8}$/.test(formatted);
  }

  static async initiateStkPush(amount, phoneNumber, accountReference = 'WeFlare-Donation') {
    try {
      console.log('🔧 MpesaService.initiateStkPush called with:', { amount, phoneNumber, accountReference });
      
      const formattedPhone = MpesaService.formatPhoneNumber(phoneNumber);
      console.log('📱 Formatted phone number:', formattedPhone);
      
      if (!MpesaService.validatePhoneNumber(phoneNumber)) {
        throw new Error('Invalid phone number format. Please use format: 254XXXXXXXXX or 07XXXXXXXX');
      }

      // Check if backend server is running
      const backendUrl = process.env.REACT_APP_MPESA_API_URL;
      console.log('🌐 Backend URL:', backendUrl);
      
      if (!backendUrl) {
        throw new Error('Backend URL not configured. Please check REACT_APP_MPESA_API_URL in .env file');
      }

      // Test backend connectivity
      try {
        console.log('🔍 Testing backend connectivity...');
        const healthCheck = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { timeout: 5000 });
        console.log('✅ Backend server is running:', healthCheck.data);
      } catch (healthError) {
        console.error('❌ Backend server connectivity test failed:', healthError.message);
        throw new Error('Backend server is not running. Please start the server with: node simple-server.js');
      }

      console.log('🌐 Making request to backend API...');

      const payload = {
        amount: Math.round(amount), // M-Pesa doesn't accept decimals
        phoneNumber: formattedPhone,
        accountReference: accountReference,
        transactionDesc: `Donation to ${process.env.REACT_APP_ORGANIZATION_NAME || 'WeFlare'} - KES ${amount}`
      };

      console.log('📦 STK Push payload for backend:', payload);

      const apiUrl = `${process.env.REACT_APP_MPESA_API_URL}/stkpush`;
      console.log('🎯 Making POST request to:', apiUrl);

      const response = await axios.post(apiUrl, payload, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ Backend response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 M-Pesa STK Push error in service:', error);
      
      if (error.response) {
        console.error('❌ Error response status:', error.response.status);
        console.error('❌ Error response data:', error.response.data);
        console.error('❌ Error response headers:', error.response.headers);
        
        // Handle specific HTTP errors
        if (error.response.status === 500) {
          throw new Error('Server error occurred. Please check backend logs and try again.');
        } else if (error.response.status === 400) {
          throw new Error(error.response.data?.error || 'Invalid request. Please check your input and try again.');
        } else if (error.response.status === 401) {
          throw new Error('Authentication failed. Please check M-Pesa credentials.');
        }
      } else if (error.request) {
        console.error('❌ No response received:', error.request);
        console.error('❌ This usually means the backend server is not running or not reachable');
        throw new Error('Cannot connect to payment server. Please ensure the backend server is running on port 5000.');
      } else if (error.code === 'ECONNABORTED') {
        console.error('❌ Request timeout');
        throw new Error('Payment request timed out. Please try again.');
      } else {
        console.error('❌ Error setting up request:', error.message);
      }
      
      throw new Error(error.response?.data?.error || error.message || 'M-Pesa payment failed. Please try again.');
    }
  }

  static async getAccessToken() {
    try {
      console.log('🔑 Getting M-Pesa access token...');
      console.log('🌐 Auth URL:', `${process.env.REACT_APP_MPESA_API_URL}/auth`);
      
      const auth = btoa(`${process.env.REACT_APP_MPESA_CONSUMER_KEY}:${process.env.REACT_APP_MPESA_CONSUMER_SECRET}`);
      
      const response = await axios.get(
        `${process.env.REACT_APP_MPESA_API_URL}/auth`,
        {
          headers: {
            'Authorization': `Basic ${auth}`
          }
        }
      );

      console.log('✅ Access token received successfully');
      return response.data.access_token;
    } catch (error) {
      console.error('💥 M-Pesa auth error:', error);
      
      if (error.response) {
        console.error('❌ Auth error status:', error.response.status);
        console.error('❌ Auth error data:', error.response.data);
      } else if (error.request) {
        console.error('❌ No auth response received - backend server may be down');
      }
      
      throw new Error('Failed to authenticate with M-Pesa');
    }
  }

  static generatePassword() {
    const timestamp = MpesaService.generateTimestamp();
    const shortcode = process.env.REACT_APP_MPESA_BUSINESS_SHORT_CODE;
    const passkey = process.env.REACT_APP_MPESA_PASSKEY;
    
    return btoa(`${shortcode}${passkey}${timestamp}`);
  }

  static generateTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }

  static async checkTransactionStatus(checkoutRequestId) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_MPESA_API_URL}/stkquery`,
        {
          BusinessShortCode: process.env.REACT_APP_MPESA_BUSINESS_SHORT_CODE,
          Password: MpesaService.generatePassword(),
          Timestamp: MpesaService.generateTimestamp(),
          CheckoutRequestID: checkoutRequestId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await MpesaService.getAccessToken()}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('M-Pesa status check error:', error);
      throw error;
    }
  }

  static pollPaymentStatus(checkoutRequestId, onStatusUpdate, maxAttempts = 12, interval = 5000) {
    let attempts = 0;
    
    const poll = async () => {
      attempts++;
      console.log(`🔄 Polling attempt ${attempts}/${maxAttempts} for checkout: ${checkoutRequestId}`);
      
      try {
        const status = await MpesaService.checkTransactionStatus(checkoutRequestId);
        console.log('📊 Status response:', status);
        
        if (status.ResultCode === '0') {
          // Payment successful
          onStatusUpdate({
            success: true,
            message: 'Payment completed successfully',
            transactionId: status.CallbackMetadata?.Item?.find(item => item.Name === 'MpesaReceiptNumber')?.Value,
            receiptNumber: status.CallbackMetadata?.Item?.find(item => item.Name === 'MpesaReceiptNumber')?.Value
          });
          return;
        } else if (status.ResultCode && status.ResultCode !== '1032') {
          // Payment failed (not pending)
          onStatusUpdate({
            failed: true,
            message: status.ResultDesc || 'Payment failed'
          });
          return;
        }
        
        // Still pending, continue polling if we haven't exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          // Timeout - assume failed
          onStatusUpdate({
            failed: true,
            message: 'Payment verification timed out. Please check your M-Pesa messages or try again.'
          });
        }
        
      } catch (error) {
        console.error('❌ Polling error:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          onStatusUpdate({
            failed: true,
            message: 'Unable to verify payment status. Please check your M-Pesa messages.'
          });
        }
      }
    };
    
    // Start polling after initial delay
    setTimeout(poll, interval);
  }
}

// Currency Conversion Service
class CurrencyService {
  static async convertUsdToKes(usdAmount) {
    try {
      // You can use a free API like exchangerate-api.com or fixer.io
      const response = await axios.get(
        `https://api.exchangerate-api.com/v4/latest/USD`
      );
      
      const kesRate = response.data.rates.KES;
      return Math.round(usdAmount * kesRate);
    } catch (error) {
      console.error('Currency conversion error:', error);
      // Fallback to approximate rate if API fails
      return Math.round(usdAmount * 130); // Approximate USD to KES rate
    }
  }

  static async convertKesToUsd(kesAmount) {
    try {
      const response = await axios.get(
        `https://api.exchangerate-api.com/v4/latest/KES`
      );
      
      const usdRate = response.data.rates.USD;
      return Math.round(kesAmount * usdRate * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error('Currency conversion error:', error);
      // Fallback to approximate rate if API fails
      return Math.round((kesAmount / 130) * 100) / 100;
    }
  }
}

// Donation Analytics Service
class DonationAnalytics {
  static trackDonationAttempt(method, amount, currency) {
    // Track donation attempts for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'donation_attempt', {
        event_category: 'donations',
        event_label: method,
        value: amount,
        currency: currency
      });
    }
  }

  static trackDonationSuccess(method, amount, currency, transactionId) {
    // Track successful donations
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'donation_success', {
        event_category: 'donations',
        event_label: method,
        value: amount,
        currency: currency,
        transaction_id: transactionId
      });
    }
  }

  static trackDonationError(method, error) {
    // Track donation errors
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'donation_error', {
        event_category: 'donations',
        event_label: method,
        value: error
      });
    }
  }
}

// Export all services as named exports
export { PayPalService, MpesaService, CurrencyService, DonationAnalytics };